/**
 * Top Customers Card Component
 *
 * Displays top spending customers with revenue and project counts.
 * Includes a "View All" button to navigate to the detailed customer portfolio page.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import type { TopCustomer } from "@/shared/types/sales-analytics";

interface TopCustomersCardProps {
  customers: TopCustomer[];
  totalCustomers: number;
  period?: string;
  title?: string;
  description?: string;
}

export function TopCustomersCard({
  customers,
  totalCustomers,
  title = "Top Spending Customers",
  description = "Highest revenue customers this period"
}: TopCustomersCardProps) {
  const navigate = useNavigate();

  if (!customers || customers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-center py-8">No customer data available</div>
        </CardContent>
      </Card>
    );
  }

  // Badge colors for top 3
  const getRankBadge = (index: number) => {
    const rank = index + 1;
    if (rank === 1) {
      return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-800 text-xs font-bold">1</span>;
    } else if (rank === 2) {
      return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold">2</span>;
    } else if (rank === 3) {
      return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">3</span>;
    }
    return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">{rank}</span>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {/* Customer list */}
        <div className="space-y-2">
          {customers.map((customer, index) => (
            <div
              key={customer.customerId}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Rank badge */}
                <div className="flex-shrink-0">
                  {getRankBadge(index)}
                </div>

                {/* Customer info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{customer.customerName}</div>
                  <div className="text-xs text-muted-foreground">
                    {customer.projectCount} {customer.projectCount === 1 ? 'project' : 'projects'}
                    {customer.mainSalesperson && (
                      <span className="ml-2">• {customer.mainSalesperson}</span>
                    )}
                  </div>
                </div>

                {/* Revenue */}
                <div className="flex-shrink-0 text-right">
                  <div className="font-semibold text-sm">฿{customer.revenue.toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All button */}
        <div className="mt-4 pt-4 border-t">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate('/analytics/customers')}
          >
            View All Customers ({totalCustomers.toLocaleString()})
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Summary */}
        <div className="mt-2 text-xs text-muted-foreground text-center">
          Showing top {customers.length} of {totalCustomers.toLocaleString()} customers
        </div>
      </CardContent>
    </Card>
  );
}
